import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Bell, Filter } from "lucide-react";
import SolicitudesItem from "../Solicitudes/SolicitudesItem/SolicitudesItem";
import PresupuestosItem from "../Solicitudes/SolicitudesItem/Presupuestos/PresupuestosItem/PresupuestosItem";
import CotizacionesItem from "../Solicitudes/SolicitudesItem/Cotizaciones/CotizacionesItem";
import ContactSolicitud from "../Solicitudes/SolicitudesItem/ContactSolicitud";
import CoincidenceSolicitud from "../Solicitudes/SolicitudesItem/CoincidenceSolicitud";
import PostLikeSolicitud from "../Solicitudes/SolicitudesItem/Posts/PostLikeSolicitud";
import PostCommentSolicitud from "../Solicitudes/SolicitudesItem/Posts/PostCommentSolicitud";
import WorkOfferAplicationSolicitud from "../Solicitudes/SolicitudesItem/OfertaDeTrabajo/WorkOfferAplicationSolicitud";
import ProjectComment from "../Solicitudes/SolicitudesItem/Proyectos/ProjectComment";
import PortfolioSolicitud from "../Solicitudes/SolicitudesItem/Portfolio/PortfolioSolicitud";
import styles from "./NotificationsPageRenderer.module.scss";

interface NotificationsPageRendererProps {
  solicitudesList: any[];
}

type NotificationFilter = 'all' | 'unread' | 'projects' | 'materials' | 'social' | 'work' | 'portfolio';

const NotificationsPageRenderer: React.FC<NotificationsPageRendererProps> = ({ solicitudesList }) => {
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [localNotifications, setLocalNotifications] = useState<any[]>([]);

  // Sincronizar estado local con Redux
  React.useEffect(() => {
    if (solicitudesList) {
      setLocalNotifications(solicitudesList);
    }
  }, [solicitudesList]);

  // Categorizar notificaciones por tipo
  const categorizedNotifications = useMemo(() => {
    if (!localNotifications.length) return { projects: [], materials: [], social: [], work: [], portfolio: [], other: [] };

    return localNotifications.reduce((acc, notification) => {
      const type = notification.type;
      
      // Proyectos/Obras (11 tipos)
      if (type.includes('POSTULACION') || type.includes('PRESUPUESTO') || 
          type.includes('COMMENT_ENVIADO') || type.includes('COMMENT_RESPONDIDO') ||
          notification?.postulacionPermiso?.status === "DENIED") {
        acc.projects.push(notification);
      }
      // Materiales (1 tipo)
      else if (type.includes('QUOTATION')) {
        acc.materials.push(notification);
      }
      // Social/Perfil (2 tipos)
      else if (type.includes('POST_') || type.includes('PROFILE_VIEW')) {
        acc.social.push(notification);
      }
      // Trabajo/Contacto (2 tipos)
      else if (type.includes('JOB_') || type.includes('CONTACT') || type.includes('COINCIDENCE')) {
        acc.work.push(notification);
      }
      // Portfolio (1 tipo)
      else if (type.includes('QUESTION')) {
        acc.portfolio.push(notification);
      }
      else {
        acc.other.push(notification);
      }
      
      return acc;
    }, {
      projects: [],
      materials: [],
      social: [],
      work: [],
      portfolio: [],
      other: []
    });
  }, [localNotifications]);

  // Filtrar notificaciones según el filtro seleccionado
  const filteredNotifications = useMemo(() => {
    if (!localNotifications.length) return [];

    let filtered = [];
    
    switch (filter) {
      case 'unread':
        filtered = localNotifications.filter(n => n.view !== true);
        break;
      case 'projects':
        filtered = categorizedNotifications.projects;
        break;
      case 'materials':
        filtered = categorizedNotifications.materials;
        break;
      case 'social':
        filtered = categorizedNotifications.social;
        break;
      case 'work':
        filtered = categorizedNotifications.work;
        break;
      case 'portfolio':
        filtered = categorizedNotifications.portfolio;
        break;
      default:
        filtered = localNotifications;
    }

    // Ordenar por fecha
    return filtered.sort((a, b) => {
      const dateA = new Date(a.sendDate).getTime();
      const dateB = new Date(b.sendDate).getTime();
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
  }, [localNotifications, filter, categorizedNotifications, sortBy]);

  // Renderizar notificación individual
  const renderNotification = (notification: any, index: number) => {
    const type = notification.type;

    // Proyectos/Obras
    if (type === "POSTULACION_SEND" || type === "USER_POSTULATION_SEND" ||
        type === "POSTULACION_NEGATED" || type === "USER_POSTULATION_NEGATED" ||
        type === "POSTULACION_ACCEPTED" || type === "USER_POSTULATION_ACCEPTED" ||
        type === "NEW_PROFILE_VIEW" || type === "USER_NEW_PROFILE_VIEW" ||
        type === "USER_COMMENT_ENVIADO" ||
        notification?.postulacionPermiso?.status === "DENIED") {
      return (
        <SolicitudesItem
          key={index}
          item={notification}
          solicitudType={type}
        />
      );
    }
    // Comentarios de proyectos
    else if (type === "COMMENT_RESPONDIDO" || type === "USER_COMMENT_RESPONDIDO") {
      return (
        <ProjectComment
          key={index}
          contact={notification}
          response={true}
        />
      );
    }
    else if (type === "COMMENT_ENVIADO") {
      return (
        <ProjectComment
          key={index}
          contact={notification}
          response={false}
        />
      );
    }
    // Cotizaciones
    else if (type === "QUOTATION_SEND") {
      return (
        <CotizacionesItem
          key={index}
          userInfo={notification?.userInfo}
          id={notification?.id}
          userSender={notification?.userSender}
          userFullName={`${notification?.userSender?.name} ${notification?.userSender?.last_name}`}
          projectName={notification?.quotation?.name}
          file={notification?.subQuotationPostulation?.file}
          projectId={notification?.quotation?.id}
          cotizacion={notification}
        />
      );
    }
    // Presupuestos
    else if (type === "PRESUPUESTO_ENVIADO") {
      return (
        <PresupuestosItem
          key={index}
          userInfo={notification.userInfo}
          presupuesto={notification}
          projectId=""
          userSender={notification.userSender}
          userFullName={`${notification.userSender.name} ${notification.userSender.last_name}`}
          subProjectDescription={notification.subProyectoPresupuesto?.descripcion}
          subProjectName={notification?.subProyectoPresupuesto?.descripcion}
          projectName={notification?.subProyectoPresupuesto?.descripcion}
          budget={notification?.subProyectoPresupuesto?.budget}
          solicitudType={type}
          type={type}
        />
      );
    }
    // Contacto
    else if (type === "NEW_CONTACT_REQUEST") {
      return (
        <ContactSolicitud
          key={index}
          contact={notification}
        />
      );
    }
    // Coincidencias
    else if (type === "NEW_COINCIDENCES") {
      return (
        <CoincidenceSolicitud
          key={index}
          contact={notification}
        />
      );
    }
    // Posts
    else if (type === "POST_LIKE_SEND") {
      return (
        <PostLikeSolicitud
          key={index}
          contact={notification}
        />
      );
    }
    else if (type === "POST_COMMENT_SEND") {
      return (
        <PostCommentSolicitud
          key={index}
          contact={notification}
        />
      );
    }
    // Trabajo
    else if (type === "NEW_JOB_APLICATION") {
      return (
        <WorkOfferAplicationSolicitud
          key={index}
          contact={notification}
        />
      );
    }
    // Portfolio
    else if (type === "QUESTION_RESPONDIDO") {
      return (
        <PortfolioSolicitud
          key={index}
          contact={notification}
        />
      );
    }

    return null;
  };

  const getFilterCount = (filterType: NotificationFilter) => {
    switch (filterType) {
      case 'unread':
        return localNotifications.filter(n => n.view !== true).length;
      case 'projects':
        return categorizedNotifications.projects.length;
      case 'materials':
        return categorizedNotifications.materials.length;
      case 'social':
        return categorizedNotifications.social.length;
      case 'work':
        return categorizedNotifications.work.length;
      case 'portfolio':
        return categorizedNotifications.portfolio.length;
      default:
        return localNotifications.length;
    }
  };

  if (!localNotifications.length) {
    return (
      <div className={styles.emptyState}>
        <Bell size={64} />
        <h3>No tienes notificaciones</h3>
        <p>Cuando recibas notificaciones, aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className={styles.notificationsPage}>
      {/* Header con filtros y acciones */}
      <div className={styles.pageHeader}>
        <div className={styles.headerInfo}>
          <h2>Notificaciones</h2>
          <span className={styles.totalCount}>
            {filteredNotifications.length} de {localNotifications.length}
          </span>
        </div>
        
        <div className={styles.headerActions}>
          <div className={styles.sortControls}>
            <label>Ordenar por:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest')}
              className={styles.sortSelect}
            >
              <option value="newest">Más recientes</option>
              <option value="oldest">Más antiguas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterTabs}>
          {[
            { id: 'all', label: 'Todas', count: getFilterCount('all') },
            { id: 'unread', label: 'No leídas', count: getFilterCount('unread') },
            { id: 'projects', label: 'Proyectos', count: getFilterCount('projects') },
            { id: 'materials', label: 'Materiales', count: getFilterCount('materials') },
            { id: 'social', label: 'Social', count: getFilterCount('social') },
            { id: 'work', label: 'Trabajo', count: getFilterCount('work') },
            { id: 'portfolio', label: 'Portfolio', count: getFilterCount('portfolio') }
          ].map(tab => (
            <button
              key={tab.id}
              className={`${styles.filterTab} ${filter === tab.id ? styles.active : ''}`}
              onClick={() => setFilter(tab.id as NotificationFilter)}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && <span className={styles.filterCount}>{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de notificaciones */}
      <div className={styles.notificationsList}>
        {filteredNotifications.map((notification, index) => (
          <div 
            key={index} 
            className={`${styles.notificationItem} ${notification.view !== true ? styles.unread : ''}`}
          >
            <div className={styles.notificationItemWrapper}>
              {renderNotification(notification, index)}
            </div>
          </div>
        ))}
      </div>

      {filteredNotifications.length === 0 && (
        <div className={styles.emptyFilterState}>
          <Filter size={48} />
          <h3>No hay notificaciones en esta categoría</h3>
          <p>Prueba con un filtro diferente</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  solicitudesList: state.dashboard.solicitudesList,
});

export default connect(mapStateToProps)(NotificationsPageRenderer);
